import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiBox,
  FiPhone,
  FiMail,
  FiMapPin,
  FiActivity,
  FiArrowLeft,
  FiSave,
  FiCheckCircle,
} from "react-icons/fi";

import {
  addOutlet,
  updateOutlet,
  getOutletById,
} from "../services/outletService";

const AddOutletLayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    outlet_name: "",
    outlet_phone: "",
    outlet_email: "",
    outlet_status: "",
    outlet_address: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadOutletData();
    }
  }, [id]);

  const loadOutletData = async () => {
    try {
      const res = await getOutletById(id);
      if (res?.status !== "success") {
        toast.error(res?.message || "Outlet terminal not found");
        return;
      }
      const outlet = res.data;
      setForm({
        outlet_name: outlet.outlet_name || "",
        outlet_phone: outlet.outlet_phone || "",
        outlet_email: outlet.outlet_email || "",
        outlet_status: outlet.outlet_status
          ? outlet.outlet_status.toUpperCase()
          : "",
        outlet_address: outlet.outlet_address || "",
      });
    } catch (error) {
      console.error(error);
      toast.error("Intelligence Link Error while loading outlet");
    }
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === "outlet_status") value = value.toUpperCase();
    if (name === "outlet_phone") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    let temp = {};
    if (!form.outlet_name.trim())
      temp.outlet_name = "Outlet designation required";
    if (!form.outlet_phone.trim())
      temp.outlet_phone = "Communication endpoint required";
    else if (form.outlet_phone.length !== 10)
      temp.outlet_phone = "Endpoint must be 10 digits";
    if (form.outlet_email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.outlet_email))
        temp.outlet_email = "Invalid transmission address";
    }
    if (!form.outlet_status)
      temp.outlet_status = "System status required";
    if (!form.outlet_address)
      temp.outlet_address = "Geospatial address required";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Coordinate validation failed");
      return;
    }
    setLoading(true);
    try {
      const res = isEdit
        ? await updateOutlet(id, form)
        : await addOutlet(form);

      if (res?.status === "success") {
        toast.success(res.message || "Outlet synchronized successfully");
        navigate("/outlets-list");
      } else {
        toast.error(res?.message || "Sync failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Intelligence Link Error");
    }
    setLoading(false);
  };

  return (
    <div className="container mt-4 mb-5 px-3 px-lg-0">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-8 col-xl-7">
          <div className="card shadow-premium border-0 animate-fade-in-up overflow-hidden rounded-4">
            {/* HEADER */}
            <div className="card-header bg-primary py-4 px-4 border-0 d-flex justify-content-between align-items-center">
               <div className="d-flex align-items-center gap-3 text-white">
                  <div className="bg-white bg-opacity-20 p-2 rounded-3">
                     <FiBox size={24} />
                  </div>
                  <div>
                     <h4 className="mb-0 fw-bold">{isEdit ? "Refine Outlet Node" : "Initialize Outlet Node"}</h4>
                     <p className="extra-small opacity-75 mb-0">Establishing structural outlet distribution parameters</p>
                  </div>
               </div>
               <Link to="/outlets-list" className="btn btn-white-soft rounded-pill btn-sm px-3 fw-bold">
                  <FiArrowLeft className="me-2"/> Back to Registry
               </Link>
            </div>

            <div className="card-body p-4 p-md-5 bg-white">
              <form onSubmit={handleSubmit} className="premium-modern-form">
                <div className="row g-4">
                  {/* OUTLET NAME */}
                  <div className="col-12">
                    <label className="form-label text-muted small fw-bold d-flex align-items-center gap-2">
                       <FiBox className="text-primary"/> Outlet Designation *
                    </label>
                    <input
                      name="outlet_name"
                      placeholder="e.g. Tactical Hub Alpha"
                      value={form.outlet_name}
                      onChange={handleChange}
                      className={`form-control form-control-lg border-0 bg-light-soft rounded-3 px-4 ${errors.outlet_name ? 'is-invalid' : ''}`}
                    />
                    {errors.outlet_name && (
                      <div className="invalid-feedback extra-small fw-bold">{errors.outlet_name}</div>
                    )}
                  </div>

                  {/* PHONE & STATUS */}
                  <div className="col-md-6">
                    <label className="form-label text-muted small fw-bold d-flex align-items-center gap-2">
                       <FiPhone className="text-primary"/> Phone Endpoint *
                    </label>
                    <input
                      name="outlet_phone"
                      placeholder="10-digit number"
                      value={form.outlet_phone}
                      onChange={handleChange}
                      className={`form-control border-0 bg-light-soft rounded-3 px-4 ${errors.outlet_phone ? 'is-invalid' : ''}`}
                    />
                    {errors.outlet_phone && (
                      <div className="invalid-feedback extra-small fw-bold">{errors.outlet_phone}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted small fw-bold d-flex align-items-center gap-2">
                       <FiActivity className="text-primary"/> Structural Status *
                    </label>
                    <select
                      name="outlet_status"
                      value={form.outlet_status}
                      onChange={handleChange}
                      className={`form-select border-0 bg-light-soft rounded-3 px-4 ${errors.outlet_status ? 'is-invalid' : ''}`}
                    >
                      <option value="">Select Status</option>
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </select>
                    {errors.outlet_status && (
                      <div className="invalid-feedback extra-small fw-bold">{errors.outlet_status}</div>
                    )}
                  </div>

                  {/* EMAIL */}
                  <div className="col-12">
                    <label className="form-label text-muted small fw-bold d-flex align-items-center gap-2">
                       <FiMail className="text-primary"/> Transmission Email
                    </label>
                    <input
                      name="outlet_email"
                      placeholder="outlet@example.com"
                      value={form.outlet_email}
                      onChange={handleChange}
                      className={`form-control border-0 bg-light-soft rounded-3 px-4 ${errors.outlet_email ? 'is-invalid' : ''}`}
                    />
                    {errors.outlet_email && (
                      <div className="invalid-feedback extra-small fw-bold">{errors.outlet_email}</div>
                    )}
                  </div>

                  {/* ADDRESS */}
                  <div className="col-12">
                    <label className="form-label text-muted small fw-bold d-flex align-items-center gap-2">
                       <FiMapPin className="text-primary"/> Geospatial Address *
                    </label>
                    <textarea
                      name="outlet_address"
                      rows="4"
                      placeholder="Physical coordinates / Hub location..."
                      value={form.outlet_address}
                      onChange={handleChange}
                      className={`form-control border-0 bg-light-soft rounded-3 px-4 py-3 ${errors.outlet_address ? 'is-invalid' : ''}`}
                    />
                    {errors.outlet_address && (
                      <div className="invalid-feedback extra-small fw-bold">{errors.outlet_address}</div>
                    )}
                  </div>

                  {/* SUBMIT BUTTON */}
                  <div className="col-12 mt-5">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-primary-gradient w-100 py-3 rounded-pill shadow-sm fw-bold d-flex align-items-center justify-content-center gap-2"
                    >
                      {loading ? (
                        <div className="spinner-border spinner-border-sm" role="status"></div>
                      ) : (
                        <>
                          <FiSave />
                          {isEdit ? "Synchronize Outlet Node" : "Deploy New Outlet to Matrix"}
                        </>
                      )}
                    </button>
                    
                    <div className="text-center mt-3">
                       <span className="extra-small text-muted d-flex align-items-center justify-content-center gap-2">
                          <FiCheckCircle className="text-success"/> System status protocol validated
                       </span>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOutletLayer;
